import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import type { User, Session, AuthChangeEvent } from '@supabase/supabase-js';
import toast from 'react-hot-toast';
import {
  clearImpersonation,
  loadImpersonation,
  saveAccountsCache,
  saveImpersonation,
  type ImpersonationUiState,
} from '../lib/impersonation';

function readIsSuperAdmin(user: User | null): boolean {
  if (!user) return false;
  const meta = (user.app_metadata || {}) as Record<string, unknown>;
  if (meta.superadmin === true || meta.superadmin === 'true') return true;
  if (meta.role === 'superadmin') return true;
  return false;
}

function readIsAdmin(user: User | null): boolean {
  if (!user) return false;
  if (readIsSuperAdmin(user)) return true;
  // Legacy display/gate during bootstrap transition
  return user.user_metadata?.role === 'admin';
}

type AuthContextType = {
  user: User | null;
  loading: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  impersonation: ImpersonationUiState | null;
  signIn: (email: string, password: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  startImpersonation: (args: {
    target_user_id: string;
    target_email: string;
    reason: string;
  }) => Promise<void>;
  /** Switch tenant while already impersonating (no exit round-trip). */
  switchImpersonation: (args: {
    target_user_id: string;
    target_email: string;
    reason: string;
  }) => Promise<void>;
  exitImpersonation: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [impersonation, setImpersonation] = useState<ImpersonationUiState | null>(() =>
    typeof window !== 'undefined' ? loadImpersonation() : null
  );
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const applyUser = useCallback((next: User | null) => {
    setUser(next);
    setIsAdmin(readIsAdmin(next));
    setIsSuperAdmin(readIsSuperAdmin(next));
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }: { data: { session: Session | null } }) => {
      applyUser(session?.user ?? null);
      // If session is gone but UI flag remains, clear it
      if (!session?.user) {
        clearImpersonation();
        setImpersonation(null);
      }
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event: AuthChangeEvent, session: Session | null) => {
      applyUser(session?.user ?? null);
      if (!session?.user) {
        clearImpersonation();
        setImpersonation(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [applyUser]);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) throw error;
    toast.success('Password reset email sent! Please check your inbox.');
  };

  const signOut = async () => {
    // Best-effort: if impersonating, mark session lost
    const imp = loadImpersonation();
    if (imp?.impersonation_id) {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData?.session?.access_token;
        if (token) {
          await fetch('/.netlify/functions/impersonate-stop', {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ impersonation_id: imp.impersonation_id }),
          });
        }
      } catch {
        /* ignore */
      }
    }
    clearImpersonation();
    setImpersonation(null);
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    navigate('/login');
  };

  const startImpersonation = async (args: {
    target_user_id: string;
    target_email: string;
    reason: string;
  }) => {
    const { data: sessionData } = await supabase.auth.getSession();
    const session = sessionData?.session;
    if (!session?.access_token || !session.refresh_token) {
      throw new Error('Not authenticated');
    }
    if (!readIsSuperAdmin(session.user)) {
      throw new Error('Superadmin required');
    }

    // Prefetch accounts while we still have a superadmin JWT (for switcher while impersonating)
    try {
      const listRes = await fetch('/.netlify/functions/admin-list-accounts?limit=50', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          Accept: 'application/json',
        },
      });
      const listJson = await listRes.json().catch(() => ({}));
      if (listRes.ok && Array.isArray(listJson.accounts)) {
        saveAccountsCache(listJson.accounts);
      }
    } catch {
      /* non-fatal */
    }

    const res = await fetch('/.netlify/functions/impersonate-start', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        target_user_id: args.target_user_id,
        reason: args.reason,
        actor_access_token: session.access_token,
        actor_refresh_token: session.refresh_token,
      }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(json?.error || `Impersonation failed (${res.status})`);
    }

    const ui: ImpersonationUiState = {
      impersonation_id: json.impersonation_id,
      target_email: json.target_email || args.target_email,
      target_user_id: json.target_user_id || args.target_user_id,
      expires_at: json.expires_at,
    };
    saveImpersonation(ui);
    setImpersonation(ui);

    const { error: setErr } = await supabase.auth.setSession({
      access_token: json.access_token,
      refresh_token: json.refresh_token,
    });
    if (setErr) {
      clearImpersonation();
      setImpersonation(null);
      throw setErr;
    }

    queryClient.clear();
    toast.success(`Viewing as ${ui.target_email}`);
    navigate('/dashboard');
  };

  const switchImpersonation = async (args: {
    target_user_id: string;
    target_email: string;
    reason: string;
  }) => {
    const imp = loadImpersonation() || impersonation;
    if (!imp?.impersonation_id) {
      // Not currently impersonating — fall back to start (requires superadmin JWT)
      await startImpersonation(args);
      return;
    }

    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;
    if (!token) {
      throw new Error('Not authenticated');
    }

    const res = await fetch('/.netlify/functions/impersonate-switch', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        impersonation_id: imp.impersonation_id,
        target_user_id: args.target_user_id,
        reason: args.reason,
      }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(json?.error || `Switch failed (${res.status})`);
    }

    const ui: ImpersonationUiState = {
      impersonation_id: json.impersonation_id,
      target_email: json.target_email || args.target_email,
      target_user_id: json.target_user_id || args.target_user_id,
      expires_at: json.expires_at,
    };
    saveImpersonation(ui);
    setImpersonation(ui);

    const { error: setErr } = await supabase.auth.setSession({
      access_token: json.access_token,
      refresh_token: json.refresh_token,
    });
    if (setErr) {
      throw setErr;
    }

    queryClient.clear();
    toast.success(`Switched to ${ui.target_email}`);
    navigate('/dashboard');
  };

  const exitImpersonation = async () => {
    const imp = loadImpersonation() || impersonation;
    if (!imp?.impersonation_id) {
      clearImpersonation();
      setImpersonation(null);
      navigate('/login');
      return;
    }

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) {
        clearImpersonation();
        setImpersonation(null);
        await supabase.auth.signOut();
        toast.error('Session lost. Please sign in as superadmin.');
        navigate('/login');
        return;
      }

      const res = await fetch('/.netlify/functions/impersonate-stop', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ impersonation_id: imp.impersonation_id }),
      });
      const json = await res.json().catch(() => ({}));

      clearImpersonation();
      setImpersonation(null);
      queryClient.clear();

      if (!res.ok || json.force_login || !json.access_token || !json.refresh_token) {
        await supabase.auth.signOut();
        toast.error(json?.error || 'Could not restore superadmin session. Sign in again.');
        navigate('/login');
        return;
      }

      const { error: setErr } = await supabase.auth.setSession({
        access_token: json.access_token,
        refresh_token: json.refresh_token,
      });
      if (setErr) {
        await supabase.auth.signOut();
        toast.error('Could not restore superadmin session. Sign in again.');
        navigate('/login');
        return;
      }

      toast.success('Exited customer view');
      navigate('/admin/accounts');
    } catch (e: any) {
      clearImpersonation();
      setImpersonation(null);
      queryClient.clear();
      try {
        await supabase.auth.signOut();
      } catch {
        /* ignore */
      }
      toast.error(e?.message || 'Exit failed. Sign in again.');
      navigate('/login');
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    isAdmin,
    isSuperAdmin,
    impersonation,
    signIn,
    resetPassword,
    signOut,
    startImpersonation,
    switchImpersonation,
    exitImpersonation,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
